/**
 * The last line of defence for "Sicht auf" being read-only (ticket 2610051): while another user is
 * shown, IRES Office refuses every change at the four places all of its writes pass through, so a
 * button the UI forgot to disable still cannot change anything.
 *
 * - Firestore: `DocumentReference.set / update / delete`, `CollectionReference.add`,
 *   `WriteBatch.commit` and `Transaction.set / update / delete` (reads, `Transaction.get` included,
 *   pass).
 * - Cloud Functions: every callable not listed in {@link VIEW_AS_READ_CALLABLES}.
 * - The IRES API (`iresAxiosInstance`): every request but a GET.
 * - Storage: `Reference.put`.
 *
 * The manager stays signed in as themselves, so the server cannot tell they are viewing as someone;
 * this guard is the only enforcement there is. Rules: docs/users/view-as.md.
 */

/** Refusal of a write while "Sicht auf" is active. */
export class ViewAsReadOnlyError extends Error {
  constructor(what: string) {
    super(`Sicht auf is read-only: refused ${what}`);
    this.name = "ViewAsReadOnlyError";
  }
}

/**
 * Callables that only read, or that act on the viewer's own session rather than as the viewed user
 * (the client / shared-user / group switches, which end "Sicht auf" anyway). Everything else is
 * refused while viewing. The IRES Admin Client's callables are left out on purpose: the admin client
 * is unavailable while viewing.
 */
export const VIEW_AS_READ_CALLABLES = new Set([
  "getArchivedProjectOverviewPageCount",
  "fetchWeatherData",
  "googleGeocode",
  "lookupAddressSuggestions",
  "createPdfReportData",
  "checkIresProjectNumberPrefix",
  "signInWithUserOfSharedEmailAddress",
  "signInWithSharedUserOfGroup",
  "generateTokenForMemberClient",
]);

/** The ASSIST proxy (`assistApiProxy`) serves reads and writes; these operations only read. */
export const VIEW_AS_READ_ASSIST_OPERATIONS = new Set([
  "getPredictions",
  "getTrend",
  "getCorrections",
  "getBackfillStatus",
]);

const ASSIST_PROXY_CALLABLE = "assistApiProxy";

/** Whether a call to the callable `name` with `data` only reads, so it may run while viewing. */
export function isCallableAllowedWhileViewing(name: string, data?: unknown): boolean {
  if (name === ASSIST_PROXY_CALLABLE) {
    const operation = (data as { operation?: unknown } | null | undefined)?.operation;

    return typeof operation === "string" && VIEW_AS_READ_ASSIST_OPERATIONS.has(operation);
  }

  return VIEW_AS_READ_CALLABLES.has(name);
}

export interface ViewAsWriteGuardContext {
  /** Whether "Sicht auf" is active right now. */
  isViewing: () => boolean;
  /** Called once per refused write, e.g. to tell the user. */
  onRefused: (error: ViewAsReadOnlyError) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any[]) => any;

/**
 * Replaces `methods` on `prototype` with versions that refuse while viewing. An async method's
 * refusal is a rejected promise, a sync one's a throw, so each fails the way its callers expect.
 */
export function guardPrototypeMethods(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prototype: any,
  methods: string[],
  label: string,
  isAsync: boolean,
  context: ViewAsWriteGuardContext
) {
  methods.forEach((method) => {
    const original: AnyFunction = prototype[method];

    prototype[method] = function (this: unknown, ...args: unknown[]) {
      if (context.isViewing()) {
        const error = new ViewAsReadOnlyError(`${label}.${method}`);
        context.onRefused(error);

        if (isAsync) {
          return Promise.reject(error);
        }

        throw error;
      }

      return original.apply(this, args);
    };
  });
}

interface CallableFactory {
  httpsCallable: AnyFunction;
}

/** Wraps `functions.httpsCallable` so every callable it hands out checks before it calls. */
export function guardCallables(functions: CallableFactory, context: ViewAsWriteGuardContext) {
  const original = functions.httpsCallable.bind(functions);

  functions.httpsCallable = (name: string, options?: unknown) => {
    const callable = original(name, options);

    return (data?: unknown) => {
      if (context.isViewing() && !isCallableAllowedWhileViewing(name, data)) {
        const error = new ViewAsReadOnlyError(`callable ${name}`);
        context.onRefused(error);

        return Promise.reject(error);
      }

      return callable(data);
    };
  };
}

interface RequestInterceptors {
  interceptors: {
    request: {
      use: (onFulfilled: (config: { method?: string }) => unknown) => unknown;
    };
  };
}

/** Refuses every IRES API request but a GET while viewing (all of its writes are PUT / POST). */
export function guardAxiosWrites(instance: RequestInterceptors, context: ViewAsWriteGuardContext) {
  instance.interceptors.request.use((config: { method?: string }) => {
    const method = (config?.method ?? "get").toLowerCase();

    if (context.isViewing() && method !== "get") {
      const error = new ViewAsReadOnlyError(`IRES API ${method.toUpperCase()}`);
      context.onRefused(error);

      return Promise.reject(error);
    }

    return config;
  });
}
